import { FC, useState } from "react"

import { Button, Stack, TextField, Typography } from "@mui/material"
import { useMutationDeleteTeacherAccount } from "api/reactQuery/mutations/deleteAccount"
import Dialog from "components/common/dialog/dialog"
import { useCustomSnackbar } from "hooks/snackbar"
import { useTranslation } from "react-i18next"
import { RoutePath } from "routes/Route.types"
import { getErrorMessageFromTab } from "utils/api"
import { clearAuthDataLocalStorage } from "utils/authData"

const CONFIRM_PHRASE = "DELETE"

const DeleteAccount: FC = () => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState("")

  const { mutate, isLoading } = useMutationDeleteTeacherAccount({
    options: {
      onSuccess: () => {
        clearAuthDataLocalStorage()
        window.location.replace(RoutePath.LOGIN)
      },
      onError: (error) => {
        showSnackbar({
          title: getErrorMessageFromTab(error),
          variant: "error",
        })
      },
    },
  })

  const handleClose = () => {
    if (isLoading) return
    setOpen(false)
    setInputValue("")
  }

  return (
    <>
      <Stack gap={1.5} alignItems="flex-start">
        <Typography
          variant="subtitle1"
          color="mockup.neutral10"
          fontWeight={600}
        >
          {t("teacherSettings.deleteAccount")}
        </Typography>
        <Typography variant="body2" color="mockup.neutral40">
          {t("teacherSettings.deleteAccountDescription")}
        </Typography>
        <Button
          type="button"
          variant="outlined"
          color="error"
          size="small"
          onClick={() => setOpen(true)}
        >
          {t("teacherSettings.deleteAccount")}
        </Button>
      </Stack>

      <Dialog
        open={open}
        onClose={handleClose}
        titleText={t("teacherSettings.deleteAccountTitle")}
        desktopMaxWidth="534px"
        tabletMaxWidth="534px"
        isLoading={isLoading}
        customActionButtonsSlot={null}
        contentWrapperProps={{ pb: 0 }}
      >
        <Typography
          variant="body2"
          color="error.main"
          textTransform="uppercase"
          fontWeight={600}
        >
          {t("teacherSettings.deleteAccountDialogHint")}
        </Typography>

        <TextField
          fullWidth
          label={t("teacherSettings.deleteAccountConfirmLabel")}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          autoComplete="off"
        />

        <Stack
          direction="row"
          gap="10px"
          justifyContent="end"
          py="16px"
          mx="-32px"
          px="32px"
          sx={(theme) => ({
            borderTop: `1px solid ${theme.palette.mockup.primary95}`,
          })}
        >
          <Button
            type="button"
            size="small"
            variant="outlined"
            onClick={handleClose}
          >
            {t("cancel")}
          </Button>
          <Button
            type="button"
            size="small"
            variant="contained"
            color="error"
            disabled={inputValue !== CONFIRM_PHRASE || isLoading}
            onClick={() => mutate()}
          >
            {t("teacherSettings.deleteAccountConfirmButton")}
          </Button>
        </Stack>
      </Dialog>
    </>
  )
}

export default DeleteAccount
